import { useEffect, useRef, useState } from "react";
import { useRoute, useLocation } from "wouter";
import {
  Undo2,
  Trophy,
  Pencil,
  X,
  Check,
  RotateCcw,
  Volume2,
  VolumeX,
  Mic,
  MicOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useGameByShareCode,
  useAddFrame,
  useUndoFrame,
  useUpdateFrame,
  useChampionship,
  useCommentaryConfig,
  resolveRosterPronouns,
  applyRosterPronouns,
  type RosterSex,
  type FrameInput,
} from "@/lib/queries";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useStickyState } from "@/lib/useStickyState";
import { useElapsed, formatDuration } from "@/lib/duration";
import {
  isSoundEnabled,
  playFourBagger,
  scoreFeedback,
  setSoundEnabled,
  undoFeedback,
  winFeedback,
} from "@/lib/feedback";
import {
  COMMENTARY_MODES,
  type CommentaryEvent,
  type CommentaryMode,
  type CommentaryResult,
  coerceMode,
  commentaryImportance,
  requestCommentary,
  setAudioSession,
  setSpeakingHandler,
  shouldSpeak,
  speakCommentary,
  stopCommentary,
  playTestSound,
  transcribeAudio,
} from "@/lib/commentary";
import { createListener, listenSupported, type Listener } from "@/lib/listen";
import { IntroCoinFlip } from "@/components/IntroCoinFlip";
import { seedNewGameForRematch } from "@/lib/newGameSticky";
import { MusicController } from "@/components/MusicController";
import type { GameFrame } from "@/lib/types";

// Marquee scoring UX: one tap commits a frame. Each team column keeps
// its running score top-aligned and a full +1..+12 pad bottom-aligned
// (12 is the per-round max: 4 bags in the hole), so the buttons sit
// under the thumb. A full-width "Tied round" button records a zero-
// point round (no team scored after cancellation). A bottom-anchored
// Scoring | Rounds switcher flips between the pad and the editable
// round-by-round detail. Undo is top-right.

const ALL_POINTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
// Both-sides gross entry includes 0 (a side can score nothing in a round).
const GROSS_POINTS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export default function Scorer() {
  const [, params] = useRoute("/score/:shareCode");
  const [, setLocation] = useLocation();
  const shareCode = params?.shareCode ?? null;
  const { data: game, refetch } = useGameByShareCode(shareCode);
  const addFrame = useAddFrame(game?.id ?? "");
  const undoFrame = useUndoFrame(game?.id ?? "");
  // Best-of-3 championship — only queried once the game completes.
  const { data: championship } = useChampionship(
    game?.id,
    game?.status === "complete",
  );
  const elapsed = useElapsed(
    game?.startedAt ?? null,
    game?.completedAt ?? null,
    game?.durationSeconds ?? null,
  );

  const [tab, setTab] = useState<"scoring" | "rounds">("scoring");
  const [error, setError] = useState<string | null>(null);
  // Both-sides scoring: this round's staged gross for each side, null
  // until the scorekeeper picks a value. Committed together as one frame.
  const [g1, setG1] = useState<number | null>(null);
  const [g2, setG2] = useState<number | null>(null);
  // Live-game sound toggle. Same persisted pref (localStorage
  // "cornholio.sound.enabled") the scoring feedback already reads, so
  // flipping it here mutes every scoreFeedback/winFeedback/undoFeedback
  // call — and stays in sync with the Profile + virtual-game toggles.
  const [soundOn, setSoundOn] = useState(isSoundEnabled);
  const sentRef = useRef(false);

  // ---- Live AI commentary ----
  const commentaryConfig = useCommentaryConfig();
  const commentaryAvailable = commentaryConfig.data?.enabled ?? false;
  const [commentaryOn, setCommentaryOn] = useStickyState(
    "scorer.commentary.on",
    false,
  );
  const [commentaryModeRaw, setCommentaryMode] = useStickyState<CommentaryMode>(
    "scorer.commentary.mode",
    "fun",
  );
  // A pre-rename value ("smack"/"offensive") persisted in localStorage would
  // otherwise leave the selector on nothing and get sent to the server.
  const commentaryMode = coerceMode(commentaryModeRaw);
  const [chattiness, setChattiness] = useStickyState<number>(
    "scorer.commentary.chattiness",
    70,
  );
  // Listen mode: hot mic that riffs on overheard table talk during lulls.
  const [listenOn, setListenOn] = useStickyState(
    "scorer.commentary.listen",
    false,
  );
  // Listen mode needs the browser mic/recorder AND a server transcription
  // (OpenAI) key — the config probe reports the latter.
  const listenAvailable =
    listenSupported() && (commentaryConfig.data?.transcribe ?? false);
  const [showMic, setShowMic] = useState(false);
  const [caption, setCaption] = useState<string | null>(null);
  // Announcer generated a line but the voice failed (e.g. ElevenLabs out of
  // credits). We keep showing captions; this banner explains why there's no
  // audio so it doesn't read as a silent app bug.
  const [voiceNotice, setVoiceNotice] = useState<string | null>(null);
  // Roster pronouns the announcer couldn't confidently infer from the name —
  // asked of the game runner once per game so the announcer says "he/she/they"
  // correctly (or nothing) instead of guessing.
  const [pronounAsk, setPronounAsk] = useState<{ id: string; name: string }[]>(
    [],
  );
  const pronounResolvedRef = useRef<string | null>(null);
  // Listen mode: the transcript shown in the mic field, whether the mic is
  // actively recording speech, and a brief "sent to announcer" flash.
  const [heardLive, setHeardLive] = useState("");
  const [heardRecording, setHeardRecording] = useState(false);
  const [heardSent, setHeardSent] = useState(false);
  // Coin-flip ceremony on a fresh live game — shown once per game, whether
  // or not the announcer is on. Winner is game.startingTeam (decided at
  // creation); the component handles the spin → land timing.
  const [coin, setCoin] = useState<{ winner: 1 | 2; label: string } | null>(
    null,
  );
  const coinShownRef = useRef<string | null>(null);
  // Last couple of spoken lines — sent to the model so it doesn't repeat.
  const recentLinesRef = useRef<string[]>([]);
  // Tracks the game we've already given a pre-game intro, so it fires
  // once per game (not on every re-render or tab switch).
  const introFiredRef = useRef<string | null>(null);
  // Bumped on every scored round. A commentary line captures this when it
  // fires; if it changed by the time the line comes back, the board has
  // moved on and the line is dropped instead of announcing a stale score.
  const roundSeqRef = useRef(0);
  // Authoritative post-score board, written on every committed round. Banter
  // reads this instead of the possibly-stale `game` from the render closure.
  const latestBoardRef = useRef<{
    team1Score: number;
    team2Score: number;
    currentRound: number;
    targetScore: number;
    winningTeam: 1 | 2 | null;
    complete: boolean;
  } | null>(null);
  // Listen mode plumbing: the live recognizer, the last time we let it
  // interject (cooldown), and a ref indirection so the recognizer always
  // calls the latest handler without being torn down on every render.
  const listenerRef = useRef<Listener | null>(null);
  const lastBanterAtRef = useRef(0);
  const onUtteranceRef = useRef<(text: string) => void>(() => {});
  const commentaryLive = commentaryAvailable && commentaryOn;
  const listenLive = commentaryLive && listenOn && listenAvailable;

  // Ask for a line (best-effort; never blocks or errors into scoring).
  // Core play-calling — any round where a team SCORED, plus notable beats
  // (4-bagger, lead change, game point) — always speaks when commentary is
  // on, so the announcer actually calls the game. The chattiness slider
  // only decides whether the quieter WASHES (nobody scored) get a line.
  // intro/banter/game-end/championship always speak.
  async function fireCommentary(
    event: CommentaryEvent,
  ): Promise<CommentaryResult | null> {
    if (!commentaryLive || !game) return null;
    if (event.kind === "round") {
      const isCorePlay =
        event.netPoints >= 1 ||
        event.fourBagger ||
        event.leadChange ||
        event.gamePoint;
      if (!isCorePlay && !shouldSpeak(commentaryImportance(event), chattiness)) {
        return null;
      }
    }
    // A line takes ~2-3s to generate and ~8s to speak. If another round
    // lands in the meantime, this line would announce a score that's no
    // longer on the board — which is exactly how stale scores (and "X
    // throws first" AFTER someone hit 21) were getting called. Remember the
    // round we fired for and drop the line if the board moved on.
    const seqAtFire = roundSeqRef.current;
    try {
      const res = await requestCommentary({
        gameId: game.id,
        mode: commentaryMode,
        recentLines: recentLinesRef.current,
        kind: event.kind,
        heard: event.heard,
        snapshot: event.snapshot,
      });
      if (event.kind === "round" && roundSeqRef.current !== seqAtFire) {
        return null; // stale — the board already moved past this round
      }
      // Remember only the FLOURISH, not the deterministic score call that
      // precedes it. Sending the whole line fed old scores and stats back into
      // the next prompt as if they were source data — the announcer recycled a
      // stale "5.4 points-per-round" from the previous line even after the
      // stat angle had rotated. Capped so an over-long line can't blow the
      // server's recentLines limit (that used to 400 every following round).
      const flourishOnly = (res.flourish ?? res.text).slice(0, 400);
      recentLinesRef.current = flourishOnly
        ? [...recentLinesRef.current, flourishOnly].slice(-2)
        : recentLinesRef.current;
      // The game-end call must never queue BEHIND stale round chatter —
      // cut whatever's mid-sentence and announce the winner now.
      if (event.kind === "game_complete" || event.kind === "championship") {
        stopCommentary();
      }
      // Voice health: the line generated, but did the audio? Surface a clear
      // reason when it didn't (out of credits), and clear it once audio is
      // flowing again — so it's never a silent, mysterious no-sound.
      if (res.voiceError) {
        setVoiceNotice(
          res.voiceError === "quota"
            ? "Announcer voice is out of credits — showing captions only. Top up ElevenLabs to restore audio."
            : "Announcer voice is unavailable right now — showing captions only.",
        );
      } else if (res.audioBase64) {
        setVoiceNotice(null);
      }
      speakCommentary(res, setCaption);
      return res;
    } catch {
      // Commentary is a garnish — swallow any failure silently.
      return null;
    }
  }

  // Overheard chatter → an announcer interjection, throttled so it never
  // spams: a per-lull cooldown plus the same chattiness probability the
  // round lines use. Kept in a ref so the recognizer reads live state.
  const BANTER_COOLDOWN_MS = 16_000;
  onUtteranceRef.current = (heard: string) => {
    if (!listenLive || !game) return;
    const now = performance.now();
    if (now - lastBanterAtRef.current < BANTER_COOLDOWN_MS) return;
    if (!shouldSpeak(0.75, chattiness)) return;
    lastBanterAtRef.current = now;
    // Flash "sent" so it's clear the snippet went to the announcer. Lasts
    // past the transcript's ~2.6s on-screen life so it's visible after.
    setHeardSent(true);
    window.setTimeout(() => setHeardSent(false), 4000);
    // Banter quotes the score too ("still leading 17 to 12"), so give it the
    // same scoreboard snapshot the round calls get. Without it the server
    // re-fetches the game — the exact race that produced wrong scores before.
    void fireCommentary({
      kind: "banter",
      heard,
      netPoints: 0,
      fourBagger: false,
      leadChange: false,
      gamePoint: false,
      snapshot: (() => {
        // Prefer the board recorded at the last committed round — `game` can
        // still be a round behind if refetch() hasn't landed yet.
        const b = latestBoardRef.current;
        const fresher =
          b && b.currentRound >= game.currentRound
            ? b
            : {
                team1Score: game.team1Score,
                team2Score: game.team2Score,
                currentRound: game.currentRound,
                targetScore: game.targetScore,
                winningTeam: (game.winningTeam as 1 | 2 | null) ?? null,
                complete: game.status === "complete",
              };
        return {
          ...fresher,
          scoringTeam: null,
          netPoints: 0,
          team1Gross: null,
          team2Gross: null,
          throwsNext: null,
        };
      })(),
    });
  };

  // A best-of-3 clinch used to fire its OWN championship line here, but the
  // completing frame already fires a game_complete finale — so the crowd heard
  // "That's the game!" twice. The clinch is now detected server-side and folded
  // into that single finale; the visual championship salute below still shows.

  // Resolve roster pronouns once per game: the server auto-sets the confident
  // ones from names and hands back anyone ambiguous for the runner to clarify,
  // so the announcer's he/she/they is right. Only when commentary is live —
  // it's purely for the announcer, and it makes an LLM call.
  useEffect(() => {
    if (!commentaryLive || !game) return;
    if (pronounResolvedRef.current === game.id) return;
    pronounResolvedRef.current = game.id;
    resolveRosterPronouns(game.id)
      .then((r) => {
        if (r.needsClarification.length) setPronounAsk(r.needsClarification);
      })
      .catch(() => {
        /* pronoun help is best-effort — never block the game */
      });
  }, [commentaryLive, game?.id]);

  // Pre-game intro. A FRESH game gets the coin-toss intro once (persisted
  // in localStorage so a page refresh before the first throw doesn't replay
  // it). A RESUMED game (rounds already scored) only gets a "welcome back"
  // intro if it's actually been idle a while — a quick refresh mid-game
  // shouldn't sound like the game is starting over. Threshold below.
  const RESUME_IDLE_MS = 20 * 60_000; // 20 min
  useEffect(() => {
    if (!commentaryLive || !game || game.status === "complete") return;
    if (introFiredRef.current === game.id) return;
    introFiredRef.current = game.id;

    const frames = game.frames ?? [];
    const fresh = frames.length === 0;
    const now = Date.now();
    const key = `cornholio.introAt.${game.id}`;
    let shouldFire = false;
    try {
      const lastIntroRaw = localStorage.getItem(key);
      const sinceIntro = lastIntroRaw ? now - Number(lastIntroRaw) : Infinity;
      if (fresh) {
        // Coin-toss intro: once per game, ever.
        shouldFire = lastIntroRaw == null;
      } else {
        // Resume intro only after a real gap since the last score AND since
        // the last intro (so back-to-back refreshes don't both replay it).
        const lastScoreAt = Date.parse(
          frames[frames.length - 1]?.createdAt ?? game.updatedAt,
        );
        const idle = now - lastScoreAt;
        shouldFire = idle > RESUME_IDLE_MS && sinceIntro > RESUME_IDLE_MS;
      }
      if (shouldFire) localStorage.setItem(key, String(now));
    } catch {
      // private mode — fire once per mount for a fresh game only.
      shouldFire = fresh;
    }
    if (!shouldFire) return;

    void fireCommentary({
      kind: "intro",
      netPoints: 0,
      fourBagger: false,
      leadChange: false,
      gamePoint: false,
    });
    // fireCommentary reads current state via closure; keyed on the game id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentaryLive, game?.id]);

  // Coin-flip ceremony — independent of the announcer. Fires once per fresh
  // live game (no rounds scored yet), landing on the pre-decided starter.
  // A localStorage flag keeps it from replaying if the scorer is reopened.
  useEffect(() => {
    if (!game || game.status === "complete") return;
    if (coinShownRef.current === game.id) return;
    const fresh = (game.frames?.length ?? 0) === 0 && !game.isVirtual;
    if (!fresh) return;
    coinShownRef.current = game.id;
    const key = `cornholio.coinShown.${game.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch {
      /* private mode — just show it */
    }
    const winner = ((game.startingTeam as 1 | 2) || 1) as 1 | 2;
    const label =
      (winner === 1 ? game.team1Players : game.team2Players)
        .map((p) => p.name)
        .join(" & ") || `Team ${winner}`;
    setCoin({ winner, label });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.id, game?.status]);

  // Silence playback when commentary is turned off or the scorer unmounts.
  useEffect(() => {
    if (!commentaryLive) stopCommentary();
    return () => stopCommentary();
  }, [commentaryLive]);

  // Listen mode: run a hot mic while it's on, feeding overheard chatter
  // to the banter handler during lulls. Torn down when toggled off / on
  // unmount. A permissions denial flips the toggle back off.
  useEffect(() => {
    if (!listenLive) {
      listenerRef.current?.stop();
      listenerRef.current = null;
      return;
    }
    const l = createListener({
      silenceMs: 1200,
      onUtterance: (t) => onUtteranceRef.current(t),
      onTranscript: (t) => setHeardLive(t),
      onRecording: (r) => setHeardRecording(r),
      onError: () => setListenOn(false),
      transcribe: (blob) => transcribeAudio(blob),
    });
    if (!l) return;
    listenerRef.current = l;
    l.start();
    return () => {
      l.stop();
      listenerRef.current = null;
      // Mic released — return the audio session to playback so media
      // (announcer/music) routes back to Bluetooth.
      setAudioSession("playback");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listenLive]);

  // Pause the hot mic while the announcer is talking so it doesn't hear
  // (and react to) its own voice.
  useEffect(() => {
    setSpeakingHandler((speaking) => {
      const l = listenerRef.current;
      if (!l) return;
      if (speaking) l.pause();
      else l.resume();
    });
    return () => setSpeakingHandler(null);
  }, []);

  if (!game) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }

  async function submitFrame(payload: FrameInput): Promise<boolean> {
    setError(null);
    scoreFeedback();
    // Pre-commit snapshot for the commentary event (lead change, etc.).
    const pre = game;
    try {
      const result = await addFrame.mutateAsync(payload);
      sentRef.current = true;
      // The board just moved — any commentary still generating for a
      // previous round is now stale and will be dropped.
      roundSeqRef.current += 1;
      // Server returns the updated game; play the bigger cue if this
      // frame ended the game so the scorekeeper gets confirmation.
      if (result?.game?.status === "complete") winFeedback();
      const post = result?.game;
      if (pre && post) {
        const n = Math.max(
          post.team1Score - pre.team1Score,
          post.team2Score - pre.team2Score,
          0,
        );
        const preLead = Math.sign(pre.team1Score - pre.team2Score);
        const postLead = Math.sign(post.team1Score - post.team2Score);
        // Which side scored this round, from the score delta.
        const scoringTeam: 1 | 2 | null =
          post.team1Score > pre.team1Score
            ? 1
            : post.team2Score > pre.team2Score
              ? 2
              : null;
        // Who held the honor before this round (last team to score, else
        // the starter) — used when this round washed.
        const priorHolder: 1 | 2 = (() => {
          let held: 1 | 2 | undefined;
          for (const f of pre.frames ?? [])
            if (f.scoringTeam != null) held = f.scoringTeam as 1 | 2;
          return held ?? ((pre.startingTeam as 1 | 2) || 1);
        })();
        // The exact scoreboard state — the announcer calls THIS, not a
        // re-fetch. Winner + who-throws-next included so the call always
        // matches the board and never misses the game-winning score.
        const snapshot = {
          team1Score: post.team1Score,
          team2Score: post.team2Score,
          currentRound: post.currentRound,
          targetScore: post.targetScore,
          scoringTeam,
          netPoints: n,
          team1Gross: payload.team1Gross ?? null,
          team2Gross: payload.team2Gross ?? null,
          winningTeam: (post.winningTeam as 1 | 2 | null) ?? null,
          complete: post.status === "complete",
          throwsNext: scoringTeam ?? priorHolder,
        };
        // Banter fires from listen mode off the `game` object, which only
        // refreshes after refetch() — so a lull right after a score sent the
        // PREVIOUS round's board ("close this 11-point gap" when it was 12).
        // Stash the authoritative post-score board for banter to use.
        latestBoardRef.current = {
          team1Score: post.team1Score,
          team2Score: post.team2Score,
          currentRound: post.currentRound,
          targetScore: post.targetScore,
          winningTeam: (post.winningTeam as 1 | 2 | null) ?? null,
          complete: post.status === "complete",
        };
        void fireCommentary({
          kind: post.status === "complete" ? "game_complete" : "round",
          netPoints: n,
          fourBagger:
            n === 12 ||
            payload.team1Gross === 12 ||
            payload.team2Gross === 12,
          leadChange: preLead !== postLead && postLead !== 0,
          gamePoint:
            post.status !== "complete" &&
            Math.min(
              post.targetScore - post.team1Score,
              post.targetScore - post.team2Score,
            ) <= 7,
          snapshot,
        });
      }
      await refetch();
      return true;
    } catch (e) {
      setError((e as ApiError).message ?? "Could not save frame");
      return false;
    }
  }

  // Net mode: one tap = one frame. A tapped 12 is a four-bagger.
  function score(team: 1 | 2 | null, points: number) {
    if (points === 12) playFourBagger();
    void submitFrame({ scoringTeam: team, netPoints: points });
  }

  // Runner answered the pronoun clarifications (or skipped). Any player left
  // unanswered stays as-is (announcer uses their name).
  async function submitPronouns(answers: { playerId: string; sex: RosterSex }[]) {
    const ask = pronounAsk;
    setPronounAsk([]);
    if (answers.length && game) {
      try {
        await applyRosterPronouns(game.id, answers);
      } catch {
        // Non-fatal — restore the prompt so the runner can retry.
        setPronounAsk(ask);
      }
    }
  }

  // Both-sides mode: commit the staged gross pair as one frame, then
  // reset the stage. A side that put up 12 gross is a four-bagger.
  async function commitRound() {
    if (g1 == null || g2 == null) return;
    if (g1 === 12 || g2 === 12) playFourBagger();
    const ok = await submitFrame({ team1Gross: g1, team2Gross: g2 });
    if (ok) {
      setG1(null);
      setG2(null);
    }
  }

  async function undo() {
    setError(null);
    undoFeedback();
    try {
      await undoFrame.mutateAsync();
      sentRef.current = false;
      // The announcer just called a round that no longer exists. Cut it off
      // mid-sentence and bump the round sequence so any line still being
      // generated for it is dropped on arrival (the stale guard in
      // fireCommentary), then own the mistake out loud — otherwise the crowd
      // is left with a score the board never had.
      stopCommentary();
      roundSeqRef.current += 1;
      void fireCommentary({
        kind: "retraction",
        netPoints: 0,
        fourBagger: false,
        leadChange: false,
        gamePoint: false,
      });
      await refetch();
    } catch (e) {
      setError((e as ApiError).message ?? "Nothing to undo");
    }
  }

  const team1Label = game.team1Players.map((p) => p.name).join(" & ") || "Team 1";
  const team2Label = game.team2Players.map((p) => p.name).join(" & ") || "Team 2";
  const nextRound = game.currentRound + 1;
  const complete = game.status === "complete";

  // Who throws first the upcoming round: cornhole "honor" goes to the
  // side that scored last; on a wash it stays with whoever held it, so
  // we walk back to the most recent scoring frame, else the game's
  // starting team for round 1.
  const throwsFirstTeam: 1 | 2 = (() => {
    const scored = (game.frames ?? [])
      .filter((f) => f.scoringTeam != null)
      .sort((a, b) => b.roundNumber - a.roundNumber);
    return (scored[0]?.scoringTeam as 1 | 2 | undefined) ?? game.startingTeam;
  })();
  return (
    // Pinned to the top of the DYNAMIC viewport (h-[100dvh] tracks the
    // mobile toolbar so the footer stays above it), and `fixed` takes the
    // scorer out of document flow so the global `body { min-height: 100dvh }`
    // can't leave the page taller than the viewport — that mismatch was
    // the phantom "~footer-height" scroll. Header + footer are shrink-0;
    // the flex-1 content absorbs the rest and scrolls internally (Rounds).
    <div className="fixed inset-x-0 top-0 h-[100dvh] bg-background flex flex-col overflow-hidden">
      {/* Pre-game coin-flip ceremony (fresh live game, announcer on). */}
      {coin && (
        <IntroCoinFlip
          winner={coin.winner}
          winnerLabel={coin.label}
          onDone={() => setCoin(null)}
        />
      )}
      <header
        className="shrink-0 border-b border-border"
        style={{ paddingTop: "max(env(safe-area-inset-top), 0px)" }}
      >
        {/* Row 1 — navigation + undo. */}
        <div className="flex items-center justify-between px-4 h-11">
          <button
            onClick={() => setLocation(`/dashboard/games/${game.id}`)}
            className="text-sm font-display uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1"
          >
            ← Game
          </button>
          <button
            onClick={undo}
            disabled={undoFrame.isPending || game.currentRound === 0}
            className="text-sm font-display uppercase tracking-wider text-muted-foreground disabled:opacity-30 inline-flex items-center gap-1.5"
          >
            <Undo2 size={16} /> Undo
          </button>
        </div>

        {/* Row 2 — the current round, its own row: large and prominent. */}
        <div className="px-4 pb-2 text-center">
          <div className="font-display uppercase tracking-widest leading-none text-2xl">
            {complete ? "Final" : `Round ${nextRound}`}
          </div>
          <div className="mt-1.5 flex items-center justify-center gap-2 text-xs font-display uppercase tracking-widest text-muted-foreground">
            {!complete && <span>to {game.targetScore}</span>}
            {elapsed.seconds > 0 && (
              <span
                className={cn(
                  "tabular-nums",
                  elapsed.live ? "text-primary" : "text-muted-foreground",
                )}
              >
                {!complete && "· "}
                {formatDuration(elapsed.seconds)}
              </span>
            )}
          </div>
        </div>

        {/* Row 3 — playback controls, their own row with big tap targets. */}
        <div className="flex items-stretch justify-around border-t border-border">
          {/* Background music player — hidden unless music tracks are
              uploaded; ducks itself while the announcer talks. */}
          <MusicController />
          {/* Live AI commentary control — opens a popover with the
              on/off, style, chattiness, and listen-mode controls. Hidden
              entirely when the server has no Gemini/ElevenLabs keys. */}
          {commentaryAvailable && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowMic((v) => !v)}
                aria-label="AI commentary settings"
                className={cn(
                  "relative flex flex-col items-center justify-center gap-0.5 px-4 py-2 min-w-[4.75rem]",
                  commentaryLive
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {commentaryLive ? <Mic size={22} /> : <MicOff size={22} />}
                <span className="text-[10px] font-display uppercase tracking-wider">
                  Announcer
                </span>
                {/* Hot-mic dot when listen mode is actively recording. */}
                {listenLive && (
                  <span className="absolute right-3 top-1.5 flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                  </span>
                )}
              </button>
              {showMic && (
                <>
                  {/* Mobile: dim backdrop so the centered modal reads as a
                      modal and taps outside close it. Desktop keeps the
                      anchored popover with no backdrop. */}
                  <div
                    className="fixed inset-0 z-40 bg-black/50 sm:hidden"
                    onClick={() => setShowMic(false)}
                  />
                  {/* Centered on mobile (was clipping off the left edge as
                      a right-anchored popover); anchored under the button
                      on sm+. */}
                  <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 sm:absolute sm:inset-auto sm:left-auto sm:right-0 sm:top-full sm:mt-1 sm:translate-x-0 sm:translate-y-0 z-50 w-[min(17rem,92vw)] rounded-lg border border-border bg-card shadow-2xl p-3 space-y-3 font-sans normal-case tracking-normal text-left">
                  <label className="flex items-center justify-between text-xs font-medium">
                    <span>AI commentary</span>
                    <button
                      type="button"
                      onClick={() => setCommentaryOn(!commentaryOn)}
                      aria-pressed={commentaryOn}
                      className={cn(
                        "h-5 w-9 rounded-full relative transition-colors shrink-0",
                        commentaryOn ? "bg-primary" : "bg-muted",
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all",
                          commentaryOn ? "left-4" : "left-0.5",
                        )}
                      />
                    </button>
                  </label>
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Style
                    </div>
                    <div className="grid grid-cols-3 gap-1">
                      {COMMENTARY_MODES.map((m) => (
                        <button
                          key={m}
                          type="button"
                          disabled={!commentaryOn}
                          onClick={() => setCommentaryMode(m)}
                          className={cn(
                            "h-8 rounded text-[11px] font-medium border disabled:opacity-40",
                            commentaryMode === m
                              ? "border-primary bg-primary/10 text-foreground"
                              : "border-border text-muted-foreground",
                          )}
                        >
                          {m === "fun"
                            ? "Fun"
                            : m === "funny"
                              ? "Funny"
                              : "Savage"}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
                      <span>Chatter</span>
                      <span>
                        {chattiness < 25
                          ? "Rare"
                          : chattiness < 60
                            ? "Some"
                            : chattiness < 85
                              ? "Lots"
                              : "Nonstop"}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={chattiness}
                      disabled={!commentaryOn}
                      onChange={(e) => setChattiness(Number(e.target.value))}
                      className="w-full accent-primary disabled:opacity-40"
                    />
                  </div>
                  {/* Listen mode — hot mic that riffs on table talk during
                      lulls. Hidden if the browser has no speech engine. */}
                  {listenAvailable && (
                    <div className="border-t border-border pt-3 space-y-1">
                      <label className="flex items-center justify-between text-xs font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          Listen mode
                          {listenLive && (
                            <span className="relative flex h-2 w-2">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          disabled={!commentaryOn}
                          onClick={() => setListenOn(!listenOn)}
                          aria-pressed={listenOn}
                          className={cn(
                            "h-5 w-9 rounded-full relative transition-colors shrink-0 disabled:opacity-40",
                            listenOn ? "bg-primary" : "bg-muted",
                          )}
                        >
                          <span
                            className={cn(
                              "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all",
                              listenOn ? "left-4" : "left-0.5",
                            )}
                          />
                        </button>
                      </label>
                      <p className="text-[10px] text-muted-foreground leading-snug">
                        Records short bits of table talk, transcribes them,
                        and jumps in during quiet moments. Mic pauses while
                        the announcer is talking.
                      </p>
                    </div>
                  )}
                  {/* Audio output test — plays a chime through the SAME path
                      as the announcer/music so you can confirm routing
                      (Bluetooth vs the phone). */}
                  <div className="border-t border-border pt-3 space-y-1">
                    <button
                      type="button"
                      onClick={() => playTestSound()}
                      className="w-full h-9 rounded-md border border-border text-xs font-medium hover:bg-muted inline-flex items-center justify-center gap-1.5"
                    >
                      <Volume2 size={14} /> Test sound
                    </button>
                    <p className="text-[10px] text-muted-foreground leading-snug">
                      Plays a chime through the app's audio so you can confirm
                      it's on your Bluetooth speaker. Listen mode captures from
                      the phone's own mic, so Bluetooth output should keep
                      playing for the audience.
                    </p>
                  </div>
                  </div>
                </>
              )}
            </div>
          )}
          {/* Sound toggle — flips the shared persisted pref that gates
              every scoring-feedback sound. Lets the scorekeeper mute
              from the boards without digging into Profile settings. */}
          <button
            type="button"
            onClick={() => {
              const next = !soundOn;
              setSoundEnabled(next);
              setSoundOn(next);
            }}
            aria-pressed={!soundOn}
            aria-label={soundOn ? "Mute sound" : "Unmute sound"}
            className="flex flex-col items-center justify-center gap-0.5 px-4 py-2 min-w-[4.75rem] text-muted-foreground hover:text-foreground"
          >
            {soundOn ? <Volume2 size={22} /> : <VolumeX size={22} />}
            <span className="text-[10px] font-display uppercase tracking-wider">
              {soundOn ? "Sound" : "Muted"}
            </span>
          </button>
        </div>
      </header>

      {/* Content area — flips between the scoring pad and the round
          detail via the bottom switcher. `relative` anchors the caption
          overlay to the top of the scoreboard, independent of header
          height. */}
      <div className="relative flex-1 flex flex-col min-h-0">
        {/* Announcer caption — floats over the scoreboard while a line
            plays; non-interactive so it never blocks the pad. */}
        {caption ? (
          <div className="absolute left-1/2 -translate-x-1/2 top-2 z-30 max-w-[92%] px-3 py-1.5 rounded-full bg-black/80 text-white text-xs text-center shadow-lg pointer-events-none flex items-center gap-1.5">
            <Mic size={11} className="text-primary shrink-0" />
            <span className="line-clamp-2">{caption}</span>
          </div>
        ) : null}
        {/* Voice-unavailable notice — dismissible; explains captions-only. */}
        {voiceNotice ? (
          <button
            type="button"
            onClick={() => setVoiceNotice(null)}
            className="absolute left-1/2 -translate-x-1/2 top-11 z-30 max-w-[92%] px-3 py-1.5 rounded-md bg-amber-500/95 text-black text-[11px] text-center shadow-lg"
          >
            {voiceNotice} <span className="underline">Dismiss</span>
          </button>
        ) : null}
        {tab === "rounds" ? (
          game.frames && game.frames.length > 0 ? (
            <FrameHistory
              frames={game.frames}
              gameId={game.id}
              team1Label={team1Label}
              team2Label={team2Label}
              bothSides={game.scoringMethod === "both_sides"}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center p-6 text-sm text-muted-foreground">
              No rounds recorded yet.
            </div>
          )
        ) : complete ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center space-y-4">
              {championship?.clinched && championship.champion ? (
                // Full-screen championship salute — a best-of-3 series
                // was just clinched.
                <div className="space-y-3">
                  <div className="text-5xl select-none animate-bounce">🏆</div>
                  <div className="font-display uppercase tracking-[0.2em] text-sm text-yellow-500">
                    Champion
                  </div>
                  <div className="font-display uppercase tracking-wide text-3xl leading-tight">
                    {championship.champion.players}
                  </div>
                  {championship.record ? (
                    <div className="text-xs text-muted-foreground font-display uppercase tracking-widest">
                      Best of 3 · series {championship.record.champion}–
                      {championship.record.opponent}
                    </div>
                  ) : null}
                  <div className="text-muted-foreground text-sm">
                    Clinched {game.team1Score}–{game.team2Score}
                  </div>
                </div>
              ) : (
                <>
                  <Trophy className="mx-auto text-primary" size={48} />
                  <div className="font-display uppercase tracking-wide text-3xl">
                    {game.winningTeam === 1 ? team1Label : team2Label}
                    <div className="text-muted-foreground text-sm mt-1 font-sans normal-case">
                      wins · {game.team1Score}–{game.team2Score}
                    </div>
                  </div>
                </>
              )}
              {/* Two-button celebration: rematch on the left (primary
                  action — most likely next step), or jump to the box
                  score for review. */}
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button
                  size="lg"
                  onClick={() => {
                    seedNewGameForRematch({
                      format: game.format,
                      team1Players: game.team1Players,
                      team2Players: game.team2Players,
                      targetScore: game.targetScore,
                    });
                    setLocation("/dashboard/games/new");
                  }}
                >
                  <RotateCcw size={14} /> Play again
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => setLocation(`/dashboard/games/${game.id}`)}
                >
                  See box score
                </Button>
              </div>
            </div>
          </div>
        ) : game.scoringMethod === "both_sides" ? (
          <>
            {/* Both-sides: stage each side's gross for the round, then
                commit one frame. The app derives the cancellation net. */}
            <div className="grid grid-cols-2 gap-px bg-border flex-1 min-h-0">
              <GrossPanel
                label={team1Label}
                score={game.team1Score}
                accent="primary"
                selected={g1}
                onSelect={setG1}
                busy={addFrame.isPending}
                throwsFirst={throwsFirstTeam === 1}
              />
              <GrossPanel
                label={team2Label}
                score={game.team2Score}
                accent="secondary"
                selected={g2}
                onSelect={setG2}
                busy={addFrame.isPending}
                throwsFirst={throwsFirstTeam === 2}
              />
            </div>

            <button
              onClick={commitRound}
              disabled={g1 == null || g2 == null || addFrame.isPending}
              className={cn(
                "h-12 border-t border-border font-display uppercase tracking-widest text-xs shrink-0 disabled:opacity-50",
                // Both sides entered → bright-yellow "ready to commit"
                // call-to-action; otherwise a muted prompt.
                g1 != null && g2 != null
                  ? "bg-yellow-400 text-black hover:bg-yellow-300"
                  : "bg-card text-muted-foreground",
              )}
            >
              {g1 == null || g2 == null
                ? "Enter both sides"
                : g1 === g2
                  ? "Commit · Tied round"
                  : `Commit · ${(g1 > g2 ? team1Label : team2Label).split(" & ")[0]} +${Math.abs(g1 - g2)}`}
            </button>

            {error && (
              <p className="text-sm text-destructive text-center py-2 shrink-0">
                {error}
              </p>
            )}
          </>
        ) : (
          <>
            {/* Team columns fill the space so each pad sits bottom-
                aligned under the thumb, score pinned to the top. */}
            <div className="grid grid-cols-2 gap-px bg-border flex-1 min-h-0">
              <TeamPanel
                label={team1Label}
                score={game.team1Score}
                accent="primary"
                onScore={(pts) => score(1, pts)}
                busy={addFrame.isPending}
                throwsFirst={throwsFirstTeam === 1}
              />
              <TeamPanel
                label={team2Label}
                score={game.team2Score}
                accent="secondary"
                onScore={(pts) => score(2, pts)}
                busy={addFrame.isPending}
                throwsFirst={throwsFirstTeam === 2}
              />
            </div>

            <button
              onClick={() => score(null, 0)}
              disabled={addFrame.isPending}
              className="h-12 border-t border-border bg-card font-display uppercase tracking-widest text-xs text-muted-foreground hover:bg-muted disabled:opacity-50 shrink-0"
            >
              · Tied round ·
            </button>

            {error && (
              <p className="text-sm text-destructive text-center py-2 shrink-0">
                {error}
              </p>
            )}
          </>
        )}
      </div>

      {/* Listen-mode mic field — shows the recording state + what Whisper
          heard; the snippet auto-sends to the announcer after a beat of
          silence (if it's worth sending). */}
      {listenLive && (
        <div className="shrink-0 border-t border-border bg-background px-3 py-2">
          <div className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2">
            <Mic
              size={16}
              className={cn(
                "shrink-0",
                heardRecording || heardLive
                  ? "text-red-500"
                  : "text-muted-foreground",
              )}
            />
            <span className="flex-1 truncate text-sm">
              {heardRecording ? (
                <span className="text-foreground">Listening…</span>
              ) : heardLive ? (
                heardLive
              ) : heardSent ? (
                <span className="text-primary">Sent to announcer ✓</span>
              ) : (
                <span className="text-muted-foreground">
                  Waiting for table talk…
                </span>
              )}
            </span>
            {(heardRecording || heardLive) && (
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
            )}
          </div>
        </div>
      )}

      {/* Bottom-anchored Scoring | Rounds switcher. */}
      <div
        className="grid grid-cols-2 border-t border-border bg-card shrink-0"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
      >
        {(["scoring", "rounds"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className={cn(
              "h-12 font-display uppercase tracking-widest text-xs transition-colors",
              tab === t
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted",
            )}
          >
            {t === "scoring" ? "Scoring" : "Rounds"}
          </button>
        ))}
      </div>
      {pronounAsk.length > 0 ? (
        <PronounPrompt players={pronounAsk} onSubmit={submitPronouns} />
      ) : null}
    </div>
  );
}

// Roster-pronoun clarification — shown to the game runner for players the
// announcer couldn't confidently gender from the name. Purely so it says
// he/she/they (or just the name) correctly; the runner can skip.
function PronounPrompt({
  players,
  onSubmit,
}: {
  players: { id: string; name: string }[];
  onSubmit: (answers: { playerId: string; sex: RosterSex }[]) => void;
}) {
  const [picks, setPicks] = useState<Record<string, RosterSex>>({});
  const options: { sex: RosterSex; label: string }[] = [
    { sex: "male", label: "He / him" },
    { sex: "female", label: "She / her" },
    { sex: "nonbinary", label: "They / them" },
    { sex: "unspecified", label: "Use name" },
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-5 space-y-4 shadow-2xl">
        <div>
          <h2 className="text-base font-display uppercase tracking-tight">
            How should the announcer refer to…
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            We couldn't tell from the name. Pick a pronoun so the announcer
            gets it right — or choose "Use name".
          </p>
        </div>
        <div className="space-y-3">
          {players.map((p) => (
            <div key={p.id} className="space-y-1.5">
              <div className="text-sm font-medium">{p.name}</div>
              <div className="grid grid-cols-4 gap-1">
                {options.map((o) => (
                  <button
                    key={o.sex}
                    type="button"
                    onClick={() =>
                      setPicks((prev) => ({ ...prev, [p.id]: o.sex }))
                    }
                    className={cn(
                      "h-8 rounded text-[10px] font-medium border px-1",
                      picks[p.id] === o.sex
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground",
                    )}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => onSubmit([])}
            className="flex-1 h-9 rounded-md border border-border text-xs text-muted-foreground"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={() =>
              onSubmit(
                Object.entries(picks).map(([playerId, sex]) => ({
                  playerId,
                  sex,
                })),
              )
            }
            disabled={Object.keys(picks).length === 0}
            className="flex-1 h-9 rounded-md bg-primary text-primary-foreground text-xs font-medium disabled:opacity-40"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// "Throwing first" pill — the side that holds the honor for the
// upcoming round (prior round's scorer). Small so it doesn't crowd
// the score.
function ThrowsFirstPill() {
  return (
    <span className="inline-flex items-center gap-1 text-[9px] font-display uppercase tracking-wider text-primary bg-primary/10 border border-primary/30 px-1.5 py-0.5 rounded-full">
      Throwing first
    </span>
  );
}

function TeamPanel({
  label,
  score,
  accent,
  onScore,
  busy,
  throwsFirst,
}: {
  label: string;
  score: number;
  accent: "primary" | "secondary";
  onScore: (pts: number) => void;
  busy: boolean;
  throwsFirst: boolean;
}) {
  return (
    // justify-between pins the score to the top and the pad to the
    // bottom; the panel flexes to fill the column height.
    <div className="flex flex-col justify-between h-full bg-card p-3 sm:p-4 gap-3">
      <div className="text-center">
        <div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground truncate">
          {label}
        </div>
        <div
          className={cn(
            "font-display tabular-nums leading-none",
            "text-[clamp(3.9rem,16.9vw,7.8rem)]",
            accent === "primary" ? "text-primary" : "text-foreground",
          )}
        >
          {score}
        </div>
        <div className="h-4 mt-1">{throwsFirst ? <ThrowsFirstPill /> : null}</div>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {ALL_POINTS.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onScore(n)}
            disabled={busy}
            className={cn(
              "h-[3.3rem] rounded-md border-2 font-display text-xl tabular-nums transition-all active:scale-95 disabled:opacity-50",
              // 12 = a four-bagger; give it the full row for emphasis.
              n === 12 ? "col-span-3" : "",
              accent === "primary"
                ? "border-primary/40 bg-primary/5 text-primary hover:bg-primary/10"
                : "border-border bg-muted/20 text-foreground hover:bg-muted",
            )}
          >
            +{n}
          </button>
        ))}
      </div>
    </div>
  );
}

// Both-sides mode: running score on top, a 0-12 selector for THIS
// round's gross on the bottom. Tapping stages the value (highlighted);
// the round isn't committed until the shared "Commit round" button.
function GrossPanel({
  label,
  score,
  accent,
  selected,
  onSelect,
  busy,
  throwsFirst,
}: {
  label: string;
  score: number;
  accent: "primary" | "secondary";
  selected: number | null;
  onSelect: (n: number) => void;
  busy: boolean;
  throwsFirst: boolean;
}) {
  return (
    <div className="flex flex-col justify-between h-full bg-card p-3 sm:p-4 gap-3">
      <div className="text-center">
        <div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground truncate">
          {label}
        </div>
        <div
          className={cn(
            "font-display tabular-nums leading-none",
            "text-[clamp(3rem,13vw,6rem)]",
            accent === "primary" ? "text-primary" : "text-foreground",
          )}
        >
          {score}
        </div>
        <div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground mt-1">
          {selected == null ? "this round" : `+${selected} this round`}
        </div>
        <div className="h-4 mt-1">{throwsFirst ? <ThrowsFirstPill /> : null}</div>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {GROSS_POINTS.map((n) => {
          const active = selected === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onSelect(n)}
              disabled={busy}
              aria-pressed={active}
              className={cn(
                "h-11 rounded-md border-2 font-display text-lg tabular-nums transition-all active:scale-95 disabled:opacity-50",
                // 12 = a four-bagger; give it the full row for emphasis.
                n === 12 ? "col-span-3" : "",
                active
                  ? accent === "primary"
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-foreground bg-foreground text-background"
                  : accent === "primary"
                    ? "border-primary/40 bg-primary/5 text-primary hover:bg-primary/10"
                    : "border-border bg-muted/20 text-foreground hover:bg-muted",
              )}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FrameHistory({
  frames,
  gameId,
  team1Label,
  team2Label,
  bothSides,
}: {
  frames: GameFrame[];
  gameId: string;
  team1Label: string;
  team2Label: string;
  bothSides: boolean;
}) {
  // Reverse-chronological so the latest round is on top of the list.
  const sorted = [...frames].sort((a, b) => b.roundNumber - a.roundNumber);
  return (
    <div className="border-t border-border bg-background flex-1 min-h-0 overflow-y-auto">
      <div className="px-3 py-2 text-[10px] font-display uppercase tracking-widest text-muted-foreground">
        Rounds — tap to edit
      </div>
      <ul className="divide-y divide-border">
        {sorted.map((f) => (
          <FrameRow
            key={f.id}
            frame={f}
            gameId={gameId}
            team1Label={team1Label}
            team2Label={team2Label}
            bothSides={bothSides}
          />
        ))}
      </ul>
    </div>
  );
}

function FrameRow({
  frame,
  gameId,
  team1Label,
  team2Label,
  bothSides,
}: {
  frame: GameFrame;
  gameId: string;
  team1Label: string;
  team2Label: string;
  bothSides: boolean;
}) {
  const [editing, setEditing] = useState(false);
  // Net-mode edit state.
  const [team, setTeam] = useState<1 | 2 | null>(frame.scoringTeam);
  const [points, setPoints] = useState<number>(frame.netPoints);
  // Both-sides edit state — seed from stored gross (derive a sane
  // fallback for any legacy both-sides frame missing one side).
  const [eg1, setEg1] = useState<number>(frame.team1Gross ?? 0);
  const [eg2, setEg2] = useState<number>(frame.team2Gross ?? 0);
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateFrame(gameId);

  function resetAndClose() {
    setError(null);
    setTeam(frame.scoringTeam);
    setPoints(frame.netPoints);
    setEg1(frame.team1Gross ?? 0);
    setEg2(frame.team2Gross ?? 0);
    setEditing(false);
  }

  async function save() {
    setError(null);
    try {
      await update.mutateAsync({
        frameId: frame.id,
        input: bothSides
          ? { team1Gross: eg1, team2Gross: eg2 }
          : { scoringTeam: team, netPoints: team === null ? 0 : points },
      });
      setEditing(false);
    } catch (e) {
      setError((e as ApiError).message ?? "Could not save");
    }
  }

  if (editing) {
    return (
      <li className="p-3 bg-muted/30 space-y-2">
        {bothSides ? (
          <div className="space-y-2">
            <GrossEditRow
              label={team1Label}
              value={eg1}
              onChange={setEg1}
            />
            <GrossEditRow
              label={team2Label}
              value={eg2}
              onChange={setEg2}
            />
            <p className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">
              {eg1 === eg2
                ? "Tied round"
                : `${(eg1 > eg2 ? team1Label : team2Label).split(" & ")[0]} +${Math.abs(eg1 - eg2)}`}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-display uppercase tracking-widest text-muted-foreground">
                R{frame.roundNumber}
              </span>
              <div className="flex-1 grid grid-cols-3 gap-1">
                <button
                  type="button"
                  onClick={() => setTeam(1)}
                  className={cn(
                    "h-9 rounded text-xs font-medium border truncate px-1",
                    team === 1
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground",
                  )}
                >
                  {team1Label.split(" & ")[0]}
                </button>
                <button
                  type="button"
                  onClick={() => setTeam(null)}
                  className={cn(
                    "h-9 rounded text-xs font-medium border",
                    team === null
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground",
                  )}
                >
                  Tied
                </button>
                <button
                  type="button"
                  onClick={() => setTeam(2)}
                  className={cn(
                    "h-9 rounded text-xs font-medium border truncate px-1",
                    team === 2
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground",
                  )}
                >
                  {team2Label.split(" & ")[0]}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPoints(n)}
                  disabled={team === null && n !== 0}
                  className={cn(
                    "h-8 w-10 rounded text-xs font-display tabular-nums border disabled:opacity-30",
                    points === n && (team !== null || n === 0)
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground",
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          </>
        )}
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={resetAndClose}>
            <X size={14} /> Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={update.isPending}>
            <Check size={14} /> {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </li>
    );
  }

  const hasGross = frame.team1Gross != null && frame.team2Gross != null;
  return (
    <li
      className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 cursor-pointer"
      onClick={() => setEditing(true)}
    >
      <span className="w-10 text-xs font-display tabular-nums text-muted-foreground">
        R{frame.roundNumber}
      </span>
      <span className="flex-1 text-sm">
        {frame.scoringTeam === null
          ? "Tied round"
          : `${frame.scoringTeam === 1 ? team1Label : team2Label} +${frame.netPoints}`}
        {hasGross && (
          <span className="text-muted-foreground tabular-nums">
            {" "}
            · {frame.team1Gross}–{frame.team2Gross}
          </span>
        )}
      </span>
      <Pencil size={12} className="text-muted-foreground opacity-50" />
    </li>
  );
}

// A compact 0-12 gross selector row used in the both-sides frame editor.
function GrossEditRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-16 shrink-0 truncate text-xs font-medium">
        {label.split(" & ")[0]}
      </span>
      <div className="flex flex-wrap items-center gap-1">
        {GROSS_POINTS.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={cn(
              "h-8 w-9 rounded text-xs font-display tabular-nums border",
              value === n
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border text-muted-foreground",
            )}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
